<template>
  <ValidationProvider :name="label" :rules="rules" :bails="true" v-slot="{ errors, valid }" :debounce="300">
    <v-text-field
      type="text"
      v-model="innerValue"
      :error-messages="!showStaticError ? (error || errors) : customRequiredError(errors)"
      :success="rules && innerValue && String(innerValue).length && valid"
      :id="label"
      :label="$t(`fields.${label}`)"
      :hint="showHint ? $t('hints.fields.telephone') : ''"
      :class="{ 'no-error-messages': toggleErrorClass && !errors.length }"
      :placeholder="placeholder ? $t(`placeholders.${placeholder}`) : undefined"
      v-bind="$attrs"
      v-on="$listeners"
      persistent-hint
      @input="onInput"
    ></v-text-field>
  </ValidationProvider>
</template>

<script lang="ts">
import Vue from "vue";
import debounce from "lodash.debounce";
import { ValidationProvider } from "vee-validate";
import { i18n } from "@/plugins/i18n";
import { ValidatorsService } from "@/service";

export default Vue.extend({
  inheritAttrs: false,
  components: {
    ValidationProvider
  },
  data: () => ({
    innerValue: "" as string | number,
    validate: (null as unknown) as (val?: string) => void,
    error: null as string | null
  }),
  props: {
    label: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      required: false
    },
    showHint: {
      type: Boolean,
      default: true
    },
    rules: {
      type: [String, Object],
      default: ""
    },
    // must be included in props
    value: {
      type: [String, Number],
      required: false
    },
    toggleErrorClass: {
      type: Boolean,
      default: false
    },
    showStaticError: {
      type: Boolean,
      default: false
    },
    phoneCharactersOnly: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    async validateNumber(val?: string) {
      if (!val || !String(val).length) { this.error = null; this.$emit("numberVerified"); return; }
      const r = await ValidatorsService.validateTelephone({ number: val });
        if (r.valid) { this.error = null; this.innerValue = r.formatted || val; this.$emit('numberVerified') }
        else { 
          this.error = !this.showStaticError ? this.errorMessage(this.label) : this.$t('customValidations.messages.notValidNumber') as string;  
          this.$emit('numberInvalid');
        }
    },
    errorMessage(field: string): string {
      const tf = i18n.t(`fields.${field}`);
      return i18n.t("customValidations.messages.telephone", {_field_: tf}) as string;
    },
    customRequiredError(errors: any) {
      if(this.error) return this.error;
      if (!errors || !errors.length || this.innerValue) return errors;
      return this.$t('placeholders.requiredPhone');
    },
    onInput(val: string) {
      const cleaned = this.phoneCharactersOnly
        ? val.replace(/[^0-9()+\-\s]/g, "")
        : val;
      this.innerValue = cleaned;
      if (cleaned !== val) {
        this.$nextTick(() => {
          this.innerValue = cleaned;
        });
      }
      this.validate(cleaned);
    },
  },
  watch: {
    // Handles internal model changes.
    innerValue(newVal) {
      this.$emit("input", newVal);
    },
    // Handles external model changes.
    value(newVal) {
      this.innerValue = newVal;
      if (!newVal) {
        this.error = null;
        this.$emit("numberVerified");
      }
    }
  },
  created() {
    this.error = null;
    if (this.value) {
      this.innerValue = this.value;
    }
    this.validate = debounce(this.validateNumber, 300, { leading: false });
  }
});
</script>
<style lang="scss" scoped>
// remove unecessary empty space of errors
.no-error-messages {
  :deep(.v-text-field__details) {
    display: none;
  }
}
</style>
